/**
 * Hook to check if user should see suspension warning dialog
 * Shows dialog for suspended users with auto-delete enabled
 */

import { useState, useEffect } from 'react';
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/authStore';

interface SuspensionWarningState {
  showDialog: boolean;
  isLoading: boolean;
  storageUsed: number;
  storageLimit: number;
  daysRemaining: number;
  suspensionStartedAt: string | null;
  suspensionDownloadExpiresAt: string | null;
  suspensionGracePeriodDays: number | null;
  suspensionAutoDelete: boolean;
}

export const useSuspensionWarning = (): SuspensionWarningState => {
  const user = useAuthStore((state) => state.user);
  const [state, setState] = useState<SuspensionWarningState>({
    showDialog: false,
    isLoading: true,
    storageUsed: 0,
    storageLimit: 0,
    daysRemaining: 0,
    suspensionStartedAt: null,
    suspensionDownloadExpiresAt: null,
    suspensionGracePeriodDays: null,
    suspensionAutoDelete: false,
  });

  useEffect(() => {
    if (!user?.id || !user?.isSuspended) {
      setState({
        showDialog: false,
        isLoading: false,
        storageUsed: 0,
        storageLimit: 0,
        daysRemaining: 0,
        suspensionStartedAt: null,
        suspensionDownloadExpiresAt: null,
        suspensionGracePeriodDays: null,
        suspensionAutoDelete: false,
      });
      return;
    }

    const checkSuspensionStatus = async () => {
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('storage_used, storage_limit, suspension_download_expires_at, suspension_started_at, suspension_auto_delete, suspension_download_enabled, suspension_grace_period_days')
          .eq('id', user.id)
          .single();

        if (error || !profile) {
          setState((prev) => ({ ...prev, isLoading: false, showDialog: false }));
          return;
        }

        // Only show dialog if:
        // 1. User is suspended
        // 2. Auto-delete is enabled
        // 3. Download access is enabled (they can still access the app)
        const shouldShow = 
          profile.suspension_auto_delete === true &&
          profile.suspension_download_enabled === true &&
          profile.suspension_download_expires_at !== null;

        if (!shouldShow) {
          setState({
            showDialog: false,
            isLoading: false,
            storageUsed: profile.storage_used || 0,
            storageLimit: profile.storage_limit || 0,
            daysRemaining: 0,
            suspensionStartedAt: profile.suspension_started_at,
            suspensionDownloadExpiresAt: profile.suspension_download_expires_at,
            suspensionGracePeriodDays: profile.suspension_grace_period_days,
            suspensionAutoDelete: profile.suspension_auto_delete || false,
          });
          return;
        }

        // Calculate days remaining
        const expiresAt = new Date(profile.suspension_download_expires_at!);
        const now = new Date();
        const daysRemaining = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        setState({
          showDialog: true,
          isLoading: false,
          storageUsed: profile.storage_used || 0,
          storageLimit: profile.storage_limit || 0,
          daysRemaining: Math.max(0, daysRemaining),
          suspensionStartedAt: profile.suspension_started_at,
          suspensionDownloadExpiresAt: profile.suspension_download_expires_at,
          suspensionGracePeriodDays: profile.suspension_grace_period_days,
          suspensionAutoDelete: profile.suspension_auto_delete || false,
        });
      } catch (error) {
        console.error('Error checking suspension status:', error);
        setState((prev) => ({ ...prev, isLoading: false, showDialog: false }));
      }
    };

    checkSuspensionStatus();

    // Subscribe to profile changes
    const channel = supabase
      .channel(`suspension-warning-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        () => {
          checkSuspensionStatus();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, user?.isSuspended]);

  return state;
};














































